import dateFormat from 'dateformat';
import { milliseconds } from 'date-fns';
import { MIN_DISTANCE_DEVIATION, MAX_DISTANCE_DEVIATION } from '#component/shipment/form/delivery/distance-deviation';
import {
  getDateTimeFormat,
  getMillisecondsFromTimeDelta,
  areAllObjectValuesEmpty,
  isInMonthRange,
  convertDistance
} from '#helper';

// RHF keeps a field array level error only as `root`, built by the resolver from `_error`
const arrayError = message => Object.assign([], { _error: message });

export default (values, props) => {
  const { delivery: displayFields, account, currentIndex } = props;
  const errors = {};
  const dateTimeFormat = getDateTimeFormat(account && account.measurementSystem);
  const measurementSystem = account?.measurementSystem;
  const isMetric = measurementSystem === 'metric';
  const minRadius = convertDistance(MIN_DISTANCE_DEVIATION, 'metric', measurementSystem);
  const maxRadius = convertDistance(MAX_DISTANCE_DEVIATION, 'metric', measurementSystem);
  const distanceUnit = isMetric ? 'meters' : 'miles';
  const originDate = values?.states?.[0]?.date;

  if (!values.states || !values.states.length || values.states.length < 1) {
    errors.states = arrayError('At least one state should be entered');
  } else if ((!originDate && !currentIndex) || (!originDate && currentIndex === 0)) {
    errors.states = arrayError('Origin should have a date');
    // eslint-disable-next-line no-underscore-dangle
    errors._error = 'Origin should have a date';
  } else {
    const statesArrayErrors = [];
    values.states.forEach((state, index, self) => {
      const stateErrors = {};

      if (displayFields[index]?.date) {
        if (!state.date) {
          stateErrors.date = { _error: 'Required' };
        } else if (state.date instanceof Date && Number.isNaN(state.date.valueOf())) {
          stateErrors.date = { _error: 'Invalid date' };
        } else {
          const nextStateWithDate = self.reduce((acc, item, i) => {
            if (!acc.date && i > index && item.date) {
              acc.date = new Date(item.date);
              acc.index = i;
            }
            return acc;
          }, { date: null, index: null });
          if (nextStateWithDate.date && new Date(state.date) > nextStateWithDate.date) {
            stateErrors.date = { _error: `Should be earlier than delivery state #${nextStateWithDate.index + 1} date: ${dateFormat(nextStateWithDate.date, dateTimeFormat)}` };
          }

          if (!isInMonthRange(new Date(self[0].date), new Date(state.date), 3)) {
            stateErrors.date = { _error: 'The difference between Origin and a state should not be longer than 3 months' };
          }
        }
        if (state.minutes) {
          const MAX_DAYS_IN_MILLISECONDS = milliseconds({ days: 7 });
          const totalMilliseconds = getMillisecondsFromTimeDelta(state.minutes);
          const isEmptyField = areAllObjectValuesEmpty(state.minutes);

          if (isEmptyField) {
            stateErrors.minutes = { _error: 'Required' };
          } else if (!isEmptyField && totalMilliseconds < 1000 * 60) {
            stateErrors.minutes = { _error: 'Minimum 1 minute' };
          } else if (totalMilliseconds > MAX_DAYS_IN_MILLISECONDS) {
            stateErrors.minutes = { _error: 'Maximum 7 days' };
          }
        }
      }

      if (displayFields[index]?.company) {
        if (state.company?.companyName && state.company.companyName.length < 2) {
          stateErrors.company = { companyName: { _error: 'Min 2 characters' } };
        } else if (state.company?.companyName?.length > 255) {
          stateErrors.company = { companyName: { _error: 'Max 255 characters' } };
        }
        if (state.company?.firstName && state.company.firstName.length < 2) {
          stateErrors.company = { firstName: { _error: 'Min 2 characters' } };
        } else if (state.company?.firstName?.length > 128) {
          stateErrors.company = { firstName: { _error: 'Max 128 characters' } };
        }
        if (state.company?.lastName && state.company.lastName.length < 2) {
          stateErrors.company = { lastName: { _error: 'Min 2 characters' } };
        } else if (state.company?.lastName?.length > 128) {
          stateErrors.company = { lastName: { _error: 'Max 128 characters' } };
        }
      }

      const hasAddressInput = Boolean(
        state?.formattedAddress ||
        state?.locations?.length ||
        (state?.address && Object.values(state.address).some(v => v && v.length))
      );

      if (displayFields[index]?.address && hasAddressInput) {
        stateErrors.address = {};
        if (!state.address) {
          stateErrors.address.address1 = { _error: 'At least one field should be filled' };
          stateErrors.formattedAddress = { _error: 'Location should be provided' };
        } else {
          if (!state.formattedAddress) {
            stateErrors.formattedAddress = { _error: 'Location should be provided' };
          }

          if (!state.radius) {
            stateErrors.radius = { _error: 'Required' };
          } else if (Number.isNaN(+state.radius)) {
            stateErrors.radius = { _error: 'Number value required' };
          } else if (+state.radius < minRadius) {
            stateErrors.radius = { _error: `Minimum ${minRadius} ${distanceUnit}` };
          } else if (+state.radius > maxRadius) {
            stateErrors.radius = { _error: `Maximum ${maxRadius} ${distanceUnit}` };
          }

          const addressEntries = Object.entries(state.address);
          if (!addressEntries.length) {
            stateErrors.address.address1 = { _error: 'At least one field should be filled' };
          } else if (addressEntries.every(entry => !entry[1])) {
            stateErrors.address.address1 = { _error: 'At least one field should be filled' };
          }

          if (state.address?.address1?.length > 128) {
            stateErrors.address.address1 = { _error: 'Max 128 characters' };
          }

          if (state.address?.address2?.length > 128) {
            stateErrors.address.address2 = { _error: 'Max 128 characters' };
          }

          if (state.address?.city?.length > 128) {
            stateErrors.address.city = { _error: 'Max 128 characters' };
          }

          if (state.address?.state?.length > 128) {
            stateErrors.address.state = { _error: 'Max 128 characters' };
          }

          if (state.address?.country?.length > 128) {
            stateErrors.address.country = { _error: 'Max 128 characters' };
          }

          if (state.address?.zipCode?.length > 16) {
            stateErrors.address.zipCode = { _error: 'Max 16 characters' };
          }
        }
      }

      if (Object.keys(stateErrors).length > 0) {
        statesArrayErrors[index] = stateErrors;
      }
    });

    const hasStateErrors = statesArrayErrors.some(err => err && Object.keys(err).length > 0);
    if (hasStateErrors) {
      errors.states = statesArrayErrors;
    }
  }
  return errors;
};
